<!--
    Notification Layout

    <NotificationLayout
      :notification="..."           # MANDATORY - notification object
      :notification-config="..."    # MANDATORY - config for notif type

      @click-trash - Fired on click on trash icon
    </NotificationLayout>
-->
<script setup lang="ts">
/* CONFIGS */
import { ALButton, ALIconButton } from '@af-lib/vue-ui';
import { computed, ref } from 'vue';

/* COMPONENTS */
import { displayGenericErrorToaster, displayGenericSuccessToaster } from '@/api/helpers/helpers';
import Text from '@/components/text/Text.vue';
import { t } from '@/config/i18n';
import SimpleBubble from '@/FNX-Lib/bubbles/SimpleBubble.vue';
/* HELPERS */
import { getTimeDifferenceText } from '@/helpers/generic';
/* TYPES*/
import type { MeNotifications } from '@/types/api/me/me';
import type { NotificationConfig } from '@/types/notifications/notification';

/* PROPS */
type Props = {
  // Notification data from API
  notification: MeNotifications;
  notificationConfig: NotificationConfig;
};
const props = defineProps<Props>();

/* EMITS */
const emits = defineEmits<{
  (e: 'click:trash'): void;
}>();

/* CONSTANTS */
const isNew = props.notification.status === 'unread';

/* COMPUTED */
const isNotifRead = computed(() => {
  return props.notification.status === 'clicked';
});

/* ACTION (CTA button inside notification) */
const isActionLoading = ref(false);

async function onActionClick() {
  const action = props.notificationConfig.action;
  if (!action) return;

  isActionLoading.value = true;
  try {
    await action.handler(props.notification);
    displayGenericSuccessToaster(t('general.export_panel.success'));
    // On successful action, remove the notification from the list
    emits('click:trash');
  } catch (error) {
    console.error(error);
    displayGenericErrorToaster();
  } finally {
    isActionLoading.value = false;
  }
}
</script>

<template>
  <div v-if="notificationConfig" class="notification-container pa-4 mb-2" :class="[{ new: isNew }]">
    <!-- Notification header with status and time -->
    <div class="d-flex justify-space-between mb-4">
      <Text
        type="body4"
        emphasis="regular"
        :fontColor="isNotifRead ? 'text-fontPrimary' : 'text-notificationUnreadText'"
      >
        {{ t(isNotifRead ? 'notifications.status.read' : 'notifications.status.unread').toUpperCase() }}
      </Text>
      <Text type="caption2" emphasis="light" fontColor="text-fontSecondary">
        {{ getTimeDifferenceText(props.notification.created_at) }}
      </Text>
    </div>
    <!-- Notification content  -->
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <!-- Notification bubble icon -->
        <SimpleBubble
          class="me-4 flex-shrink-0"
          :color="notificationConfig?.color(props.notification)"
          width="40px"
          height="40px"
        >
          <v-icon class="text-icon-4" :icon="notificationConfig?.class(props.notification)" color="white"></v-icon>
        </SimpleBubble>
        <!-- Notification text and trash icon -->
        <div class="d-flex flex-column gap-4">
          <Text type="body3" class="notif-text flex-grow-0">
            {{ notificationConfig.translationKey(props.notification) }}
          </Text>
          <!-- Optional action button -->
          <ALButton
            v-if="notificationConfig.action"
            size="s"
            type="tertiary"
            class="w-min"
            :label="notificationConfig.action.label(props.notification)"
            :icon="notificationConfig.action?.icon"
            :isLoading="isActionLoading"
            @click.stop="onActionClick"
          />
        </div>
      </div>
      <v-sheet color="transparent" class="align-self-start">
        <ALIconButton
          variant="tertiary"
          size="s"
          icon="fa fa-trash"
          :label="t('general.delete')"
          @click.stop="emits('click:trash')"
        />
      </v-sheet>
    </div>
  </div>
</template>
<style scoped lang="scss">
.notif-text {
  max-width: 312px;
}
</style>
